import { useState, useEffect } from 'react';
import { fetchDenialReasons } from '../../lib/taxonomy';
import Overlay from '../Overlay';
import ActionButton from '../ActionButton';

// src/components/inventory/DenyReasonSheet.js
// Issue #397, Session 78n4. Approvals Gate decisions D7, D8 and D9.
//
// ── What this is, in plain terms ──
//
// A manager pressing Deny used to be a single tap that recorded nothing. The
// requester got a notification saying no, with no reason, and nowhere to find
// one. The predictable result is the same request raised again the next morning.
//
// Deny is also the ONLY decision path in Stede that sends no email, so that one
// in-app notification is the entire message a denied requester ever receives.
// A missing reason costs more here than it would anywhere else in the app.
//
// So Deny now opens this: a REQUIRED reason from a short list, and an OPTIONAL
// note.
//
// ── Why a list and not just a text box (D7) ──
//
// Free text cannot be counted. "How often do we deny because it is already in
// stock at another branch" is the question that justifies building inter-branch
// transfers (#371), and only a code can answer it. But a code alone is too
// blunt for the person reading it: "available elsewhere" without "there are two
// boxes on the Houston rack" just sends somebody hunting.
//
// Requiring the code and NOT the note is what keeps it honest. A required
// free-text box gets "no" typed into it; an optional one gets used when there
// is something worth saying.
//
// ── One component, both call sites (D9) ──
//
// The Deny button exists twice on the Requests inbox — the desktop detail panel
// and the mobile bottom sheet — and they already share one handler. This sheet
// is mounted once by the page rather than once per surface. Two copies of a
// sheet carrying a REQUIRED field is two chances for the requirement to drift,
// and a required field that is only required on one of two screens is worse
// than no requirement at all.
//
// Overlay with variant switching rather than position: fixed — locked rule 3.
export default function DenyReasonSheet({ req, isMobile, onCancel, onConfirm, saving }) {
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reasonId, setReasonId] = useState('');
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);

  // Read through lib/taxonomy rather than querying here. Session 78n5 added the
  // governed list to Company Settings, and two places deciding independently
  // which reasons are "offered" is exactly the drift the taxonomy module exists
  // to prevent -- the retired-reason rule in particular has to be identical on
  // both, or a manager is offered a reason the settings screen has retired.
  //
  // The error is CAUGHT rather than dropped, and that is the substantive change
  // rather than tidying. The previous version destructured `data` alone, so a
  // failed fetch rendered an empty dropdown -- and because the reason is
  // REQUIRED, an empty dropdown means Deny can never be completed, with nothing
  // on screen saying why. Silently-empty is the failure mode this codebase keeps
  // naming; here it disables a decision path rather than merely hiding a list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { rows, error } = await fetchDenialReasons({ activeOnly: true });
      if (cancelled) return;
      if (error) setLoadError('Could not load the reason list: ' + error.message);
      setReasons(rows);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const chosen = reasons.find(r => String(r.id) === String(reasonId));

  function confirm() {
    setTouched(true);
    if (!chosen) return;
    // The LABEL travels with the id, deliberately. The id is what makes denials
    // countable; the label is what the reason SAID at the moment it was chosen,
    // so renaming it next year cannot rewrite what a manager told somebody last
    // year. Same reasoning as unit_cost_at_transaction, and the migration header
    // explains why this is history rather than the copied-key bug this session
    // spent its time removing.
    onConfirm({
      reasonId: chosen.id,
      reasonLabel: chosen.name,
      note: note.trim() || null,
    });
  }

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={onCancel} maxWidth={460}>
      <div style={{ padding: '20px 20px 16px' }}>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: 17, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
          Deny request
        </div>
        <div style={{ fontSize: 13, color: '#6A6A6A', marginTop: 4 }}>
          {req?.request_number}
          {' — the requester sees this reason, and it is the only message they get.'}
        </div>

        <div style={{ marginTop: 16 }}>
          <label className="form-label">Reason *</label>
          <select
            className="form-select"
            value={reasonId}
            onChange={e => setReasonId(e.target.value)}
            disabled={loading}
          >
            <option value="">{loading ? 'Loading…' : 'Choose a reason…'}</option>
            {reasons.map(r => (
              <option key={r.id} value={String(r.id)}>{r.name}</option>
            ))}
          </select>
          {loadError && (
            <div style={{ fontSize: 12, color: '#E24B4A', marginTop: 5 }}>
              {loadError}
            </div>
          )}
          {!loading && !loadError && reasons.length === 0 && (
            <div style={{ fontSize: 12, color: '#E24B4A', marginTop: 5 }}>
              No denial reasons are active. Add one in Company Settings before denying.
            </div>
          )}
          {/* #492, Session 79.1 — SHOWN BEFORE THE PRESS, NOT AFTER IT.
              This line used to wait for `touched`, so it only appeared once
              somebody had already pressed a button that did nothing — which is
              scolding rather than helping. With the button now disabled until a
              reason is chosen, this is the text that says WHY it is disabled, and
              it has to be visible at the moment they are deciding.
              `touched` still colours it: quietly grey as guidance, red once they
              have pressed. Same words either way — a message that changes wording
              under pressure reads as a different problem. */}
          {!chosen && !loading && !loadError && reasons.length > 0 && (
            <div style={{ fontSize: 12, color: touched ? '#E24B4A' : '#6A6A6A', marginTop: 5 }}>
              Pick a reason before denying. This is what the requester will be told.
            </div>
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          <label className="form-label">Note (optional)</label>
          <textarea
            className="form-input"
            rows={3}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="e.g. there are two boxes on the Houston rack"
            style={{ resize: 'vertical', minHeight: 72 }}
          />
          <div style={{ fontSize: 12, color: '#6A6A6A', marginTop: 5, lineHeight: 1.4 }}>
            The reason above is counted. The note is what makes it useful to the
            person reading it.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={!!saving} style={{ flex: 1 }}>
            Cancel
          </button>
          {/* #492, Session 79.1 — DISABLED UNTIL A REASON IS CHOSEN.
              A manager pressed this with no reason selected and read the press as
              a dead button: the request stayed pending, correctly, and the
              refusal was easy to miss.
              CORRECTION TO #492's OWN PREMISE, recorded because the next reader
              will trust whichever they open first: the entry says "no message
              appears". A message HAS existed since Session 78n5 — the `touched`
              line under the dropdown. It is 12px of red text beside the select
              while the eye is on the button 150px below it, so the honest reading
              is that it was shown and not seen, not that it was absent.
              That makes disabling the right fix rather than merely the chosen one.
              The sheet already marks Reason with an asterisk; a disabled button
              confirms what the asterisk claims BEFORE the press, instead of
              waiting to scold after it. The message stays for the case where a
              reason is picked and then cleared. */}
          <ActionButton
            className="btn btn-danger"
            onClick={confirm}
            blockedReason={
              loading || loadError || reasons.length === 0 || chosen
                ? null
                : 'Pick a reason before denying. This is what the requester will be told.'
            }
            reasonAlreadyShown
            busy={!!saving || loading}
            busyLabel={saving === 'deny' ? 'Denying…' : 'Deny request'}
            style={{ flex: 1 }}
          >
            {saving === 'deny' ? 'Denying…' : 'Deny request'}
          </ActionButton>
        </div>
      </div>
    </Overlay>
  );
}
